import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { ChevronDown, ImagePlus, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiErrorDetail } from "@/lib/api";
import { ConfirmDelete } from "@/components/admin/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["New", "Confirmed", "Ready", "Completed", "Cancelled"];
const input = "w-full rounded-lg border border-white/10 bg-[#07090E] px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60";
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail?.message || e.response?.data?.detail));
const blank = { name: "", description: "", price: "", stock: 0, active: true, image: null, image_url: null };

const ProductDialog = ({ appId, product, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF(product?.id ? { ...product, price: product.price, image: product.image_path, image_url: product.image } : blank);
  }, [product]);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post(`/manage/apps/${appId}/files`, fd); setF((x) => ({ ...x, image: r.data.path, image_url: r.data.url })); } catch (x) { err(x); }
  };
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = { name: f.name, description: f.description, price: Number(f.price), stock: Number(f.stock), active: f.active, image: f.image };
    try {
      if (product.id) await api.patch(`/manage/apps/${appId}/store/products/${product.id}`, body);
      else await api.post(`/manage/apps/${appId}/store/products`, body);
      toast.success("Product saved"); onSaved();
    } catch (x) { err(x); }
    setBusy(false);
  };
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-dialog" className="border-white/10 bg-[#0D111A] text-white">
        <DialogHeader><DialogTitle>{product?.id ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <input required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="product-name-input" className={input} />
          <textarea rows={3} placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="product-description-input" className={input} />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-400">Price ($)<input required type="number" min="0" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="product-price-input" className={input} /></label>
            <label className="text-xs text-slate-400">Stock<input required type="number" min="0" step="1" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} data-testid="product-stock-input" className={input} /></label>
          </div>
          <div className="flex items-center gap-3">
            {f.image_url && <img src={assetUrl(f.image_url)} alt="" className="h-14 w-20 rounded border border-white/10 object-cover" />}
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-200 hover:border-cyan-400/50"><ImagePlus className="h-3.5 w-3.5" />{f.image ? "Replace image" : "Upload image"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="product-image-input" /></label>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="product-active-checkbox" className="h-4 w-4 accent-cyan-400" />Show in the store</label>
          <button type="submit" disabled={busy} data-testid="product-save-btn" className="w-full rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">Save product</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ProductsPanel = ({ appId }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const load = useCallback(() => api.get(`/manage/apps/${appId}/store/products`).then((r) => setItems(r.data)).catch(err), [appId]);
  useEffect(() => { load(); }, [load]);
  const remove = async () => { try { await api.delete(`/manage/apps/${appId}/store/products/${deleting.id}`); setDeleting(null); toast.success("Product deleted"); load(); } catch (x) { err(x); } };
  return (
    <div data-testid="products-panel">
      <button onClick={() => setEditing({})} data-testid="product-add-btn" className="mb-3 inline-flex items-center gap-1 rounded-full bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300"><Plus className="h-4 w-4" />Add product</button>
      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#0B0F18]">
        <table className="w-full min-w-[620px] text-left text-sm">
          <thead className="border-b border-white/5 text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3 font-medium">Product</th><th className="px-4 py-3 font-medium">Price</th><th className="px-4 py-3 font-medium">Stock</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3" /></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-white/5 text-slate-300 last:border-0">
                <td className="px-4 py-3"><div className="flex items-center gap-3">{p.image ? <img src={assetUrl(p.image)} alt="" className="h-10 w-14 rounded border border-white/10 object-cover" /> : <div className="h-10 w-14 rounded border border-dashed border-white/10" />}<span className="text-white">{p.name}</span></div></td>
                <td className="px-4 py-3">{p.price_label}</td>
                <td data-testid={`product-stock-${p.id}`} className={`px-4 py-3 ${p.stock === 0 ? "text-amber-300" : ""}`}>{p.stock}</td>
                <td className="px-4 py-3">{p.active ? <span className="text-emerald-300">Active</span> : <span className="text-slate-500">Hidden</span>}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <button onClick={() => setEditing(p)} aria-label="Edit product" data-testid={`product-edit-${p.id}`} className="p-1.5 text-slate-400 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => setDeleting(p)} aria-label="Delete product" data-testid={`product-delete-${p.id}`} className="p-1.5 text-slate-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={5} data-testid="products-empty" className="px-4 py-10 text-center text-slate-500">No products yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <ProductDialog appId={appId} product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <ConfirmDelete target={deleting} label={deleting ? `product ${deleting.name}` : ""} onCancel={() => setDeleting(null)} onConfirm={remove} />
    </div>
  );
};

const OrderRow = ({ o, onStatus }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr data-testid={`order-row-${o.id}`} className="border-b border-white/5 text-slate-300">
        <td className="px-4 py-3"><button onClick={() => setOpen(!open)} data-testid={`order-expand-${o.id}`} className="inline-flex items-center gap-1 text-white"><ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />#{o.number}</button></td>
        <td className="px-4 py-3 text-xs text-slate-500">{format(new Date(o.created_at), "MMM d, h:mm a")}</td>
        <td className="px-4 py-3"><div className="text-white">{o.customer.name}</div><div className="text-xs text-slate-500">{o.customer.email} · {o.customer.phone}</div></td>
        <td data-testid={`order-total-${o.id}`} className="px-4 py-3 text-white">{o.total_label}</td>
        <td className="px-4 py-3">
          <select value={o.status} disabled={o.status === "Cancelled"} onChange={(e) => onStatus(o, e.target.value)} data-testid={`order-status-${o.id}`} className="rounded-lg border border-white/10 bg-[#07090E] px-2 py-1 text-sm text-white disabled:opacity-60">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </td>
      </tr>
      {open && (
        <tr className="border-b border-white/5 bg-white/[0.02]"><td colSpan={5} className="px-6 py-3 text-sm text-slate-300">
          {o.items.map((i) => <div key={i.product_id} className="flex justify-between py-0.5"><span>{i.qty} × {i.name} <span className="text-slate-500">@ {i.price_label}</span></span><span>{i.line_total_label}</span></div>)}
          {o.notes && <p className="mt-2 text-xs text-slate-400">Notes: {o.notes}</p>}
        </td></tr>
      )}
    </>
  );
};

export const OrdersPanel = ({ appId }) => {
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [confirmCancel, setConfirmCancel] = useState(null);
  const load = useCallback(() => api.get(`/manage/apps/${appId}/store/orders`, { params: { status: status || undefined } }).then((r) => setData(r.data)).catch(err), [appId, status]);
  useEffect(() => { load(); }, [load]);
  const apply = async (o, s) => {
    try { await api.patch(`/manage/apps/${appId}/store/orders/${o.id}`, { status: s }); toast.success(s === "Cancelled" ? `Order #${o.number} cancelled — stock restored` : `Order #${o.number} is now ${s}`); load(); }
    catch (x) { err(x); }
  };
  const onStatus = (o, s) => (s === "Cancelled" ? setConfirmCancel(o) : apply(o, s));
  return (
    <div data-testid="orders-panel">
      <div className="mb-3 flex flex-wrap gap-2">
        {["", ...STATUSES].map((s) => <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`orders-filter-${s || "all"}`} className={`rounded-full border px-3 py-1 text-xs ${status === s ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-300"}`}>{s || "All"}</button>)}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#0B0F18]">
        <table className="w-full min-w-[680px] text-left text-sm">
          <thead className="border-b border-white/5 text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3 font-medium">Order</th><th className="px-4 py-3 font-medium">Placed</th><th className="px-4 py-3 font-medium">Customer</th><th className="px-4 py-3 font-medium">Total</th><th className="px-4 py-3 font-medium">Status</th></tr></thead>
          <tbody>
            {data.items.map((o) => <OrderRow key={o.id} o={o} onStatus={onStatus} />)}
            {!data.items.length && <tr><td colSpan={5} data-testid="orders-empty" className="px-4 py-10 text-center text-slate-500">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <ConfirmDelete target={confirmCancel} title={confirmCancel ? `Cancel order #${confirmCancel.number}?` : ""} description="Its items go back into stock. A cancelled order can't be reopened." action="Cancel order" onCancel={() => setConfirmCancel(null)} onConfirm={() => { apply(confirmCancel, "Cancelled"); setConfirmCancel(null); }} />
    </div>
  );
};
